import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { CreateUserForm } from '../components/users/CreateUserForm';
import { EditUserForm } from '../components/users/EditUserForm';
import { UsersList } from '../components/users/UsersList';
import { User, Property } from '../types/user';
import { getUsers, deleteUser } from '../services/users';
import { getProperties } from '../services/properties';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Dialog } from '@headlessui/react';
import toast from 'react-hot-toast';

export const UsersPage = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const fetchData = async () => {
    try {
      const [usersData, propertiesData] = await Promise.all([
        getUsers(),
        getProperties()
      ]);
      setUsers(usersData);
      setProperties(propertiesData);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) return;

    try {
      // Primero realizamos la eliminación en el servidor
      await deleteUser(userId);
      
      // Luego actualizamos la UI después de confirmar que la eliminación fue exitosa
      setUsers(currentUsers => currentUsers.filter(user => user.id !== userId));
      toast.success('Usuario eliminado exitosamente');
      
      // Refresh data from server to ensure consistency
      // Añadimos un pequeño retraso para asegurar que la base de datos se actualice
      setTimeout(async () => {
        await fetchData();
      }, 1000);
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      toast.error('Error al eliminar el usuario');
      
      // If there was an error, refresh to get the current state
      await fetchData();
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Gestión de Usuarios</h1>
          <Button onClick={() => setShowCreateModal(true)}>
            Crear Nuevo Usuario
          </Button>
        </div>

        <Card>
          <UsersList
            users={users}
            onEdit={handleEditUser}
            onDelete={handleDeleteUser}
          />
        </Card>

        {/* Modal de Crear Usuario */}
        <Dialog
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          className="fixed inset-0 z-10 overflow-y-auto"
        >
          <div className="flex items-center justify-center min-h-screen">
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />

            <div className="relative bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <Dialog.Title className="text-lg font-semibold mb-4">
                Crear Nuevo Usuario
              </Dialog.Title>

              <CreateUserForm
                properties={properties}
                onUserCreated={async () => {
                  setShowCreateModal(false);
                  await fetchData();
                }}
              />
            </div>
          </div>
        </Dialog>

        {/* Modal de Editar Usuario */}
        <Dialog
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          className="fixed inset-0 z-10 overflow-y-auto"
        >
          <div className="flex items-center justify-center min-h-screen">
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />

            <div className="relative bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <Dialog.Title className="text-lg font-semibold mb-4">
                Editar Usuario
              </Dialog.Title>

              {selectedUser && (
                <EditUserForm
                  user={selectedUser}
                  properties={properties}
                  onUserUpdated={async () => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                    await fetchData();
                  }}
                  onCancel={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                  }}
                />
              )}
            </div>
          </div>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};